import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuArrowRight, LuShieldCheck, LuClock, LuKeyRound, LuCheck } from 'react-icons/lu';
import HeaderOne from '../../../header/HeaderOne';
import FooterOne from '../../../footer/FooterOne';
import Head from '../../../components/Head';
import '../../landing.css';

const FEATURES = [
  { icon: <LuKeyRound size={16} strokeWidth={2} />, title: 'We generate + match the code', body: 'No need to store codes server-side. POST a number, we send a 4–6 digit code. POST it back, we tell you if it matches.' },
  { icon: <LuClock size={16} strokeWidth={2} />, title: 'Configurable expiry', body: 'Default 5 minutes. Set anywhere from 60 seconds to an hour per request.' },
  { icon: <LuShieldCheck size={16} strokeWidth={2} />, title: 'Rate-limited per number', body: 'Built-in protection against verification floods. Max 5 codes per number per hour by default.' },
  { icon: <LuCheck size={16} strokeWidth={2} />, title: 'Custom message template', body: 'Brand the SMS however you want — "Your {{brand}} code is {{code}}". Code placeholder is the only required field.' },
];

export default function VerificationApi() {
  const navigate = useNavigate();

  return (
    <>
      <Head
        title="SMS Verification API — OTP & 2FA via Your SIM"
        description="OTP and 2FA SMS verification through your own SIM. TextLink generates and matches the code — you just POST a phone number and POST it back. $0.001 per verification, no per-OTP markup."
        canonical="/verificationapi"
      />
      <HeaderOne />

      <main className="lp">
        <div className="lp-shell">

          <header className="lp-hero">
            <span className="lp-eyebrow">Verification API</span>
            <h1 className="lp-h1">
              OTP & 2FA over SMS.<br />
              <span className="lp-h1-accent">Two endpoints. Done.</span>
            </h1>
            <p className="lp-lead">
              Verify phone numbers without writing your own code-storage logic. We generate, deliver, and match the OTP — you just POST a number and get a token back.
            </p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Get an API key <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <a className="lp-btn lp-btn--ghost" href="https://docs.textlinksms.com/" target="_blank" rel="noopener noreferrer">
                Read the docs
              </a>
            </div>
            <div className="lp-meta">
              <span>$0.001 per verification</span>
              <span className="lp-meta-sep" />
              <span>No code storage</span>
              <span className="lp-meta-sep" />
              <span>180+ countries</span>
            </div>
          </header>

          <section className="lp-section">
            <h2 className="lp-h2">Two-step flow</h2>
            <p className="lp-sub">Send the code, then verify what the user typed back.</p>

            <pre className="lp-code"><code>{`# 1. Send the code
curl -X POST https://textlinksms.com/api/send-verification \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{ "phone_number": "+15551234567" }'

# Returns: { "verification_id": "ver_abc123" }


# 2. User types the code → verify it
curl -X POST https://textlinksms.com/api/check-verification \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "verification_id": "ver_abc123",
    "code": "8472"
  }'

# Returns: { "verified": true }`}</code></pre>
          </section>

          <section className="lp-section">
            <h2 className="lp-h2">What you get</h2>
            <p className="lp-sub">Production-ready OTP without the boilerplate.</p>
            <ul className="lp-feat-list">
              {FEATURES.map((f, i) => (
                <li key={i} className="lp-feat">
                  <div className="lp-feat-icon">{f.icon}</div>
                  <div className="lp-feat-text">
                    <strong>{f.title}</strong>
                    <span>{f.body}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="lp-section">
            <h2 className="lp-h2">Why this beats SMS-based 2FA from Twilio</h2>
            <p className="lp-sub">
              Twilio Verify charges $0.05 per attempt on top of carrier fees. With TextLink, OTPs go through your own SIM at $0.001 — and there's no 10DLC paperwork to register your sender. Same delivery rate, 50× cheaper.
            </p>
            <div className="lp-callout">
              <strong>Don't want managed OTP?</strong> Use the regular <Link to="/smsapi" style={{ color: 'inherit', textDecoration: 'underline' }}>SMS API</Link> and store codes yourself. Same price either way.
            </div>
          </section>

          <section className="lp-cta">
            <h2 className="lp-h2">Wire up OTP in 5 minutes.</h2>
            <p className="lp-sub">300 free messages to test. No credit card.</p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/pricing">See pricing</Link>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
